import { httpClient } from '@/shared/utils/http-client'

const SCOUT_SERVICE_BASE_URL = 'https://scout-service-v2.enter.de'

interface ConfirmAppointmentRequest {
  appointmentUuid: string
  scoutUuid: string
  option: number
  startTime: string
  endTime: string
}

interface ConfirmAppointmentResponse {
  success: boolean
  message?: string
  data?: unknown
}

export const confirmScoutAppointment = async (
  request: ConfirmAppointmentRequest
): Promise<ConfirmAppointmentResponse> => {
  try {
    const { appointmentUuid, scoutUuid, option, startTime, endTime } = request

    const url = `${SCOUT_SERVICE_BASE_URL}/user/v1/public/onsite-appointment/scheduling/${appointmentUuid}?scoutUuid=${scoutUuid}&option=${option}`

    const response = await httpClient.fetch(url, {
      method: 'POST',
      body: JSON.stringify({
        startTime,
        endTime,
      }),
      skipAuth: true, // Public endpoint
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()

    return {
      success: true,
      data,
    }
  } catch (error) {
    console.error('Error confirming scout appointment:', error)

    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown error occurred',
    }
  }
}

interface ApiResponse {
  code: number
  data: {
    uuid: string
    buildingUUID: string
    scheduleStartTime: string | null
    createdAt: string
    updatedAt: string
    customerAccountUUID: string | null
    scoutUUID: string
    scoutName: string
    status: string
    sc2Url: string | null
    customerAddress: string
    customerName: string
    customerPhone: string
    customerEmail: string
    dataCollected: boolean
    residentialUnits: number
    optionDate: string
    optionTimeRange: string
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export const getAppointmentDetails = async (
  appointmentUuid: string,
  scoutUuid: string,
  option: number
): Promise<ApiResponse> => {
  try {
    const url = `${SCOUT_SERVICE_BASE_URL}/user/v1/public/onsite-appointment/scheduling/${appointmentUuid}?scoutUuid=${scoutUuid}&option=${option}`

    const response = await httpClient.fetch(url, {
      method: 'GET',
      skipAuth: true, // Public endpoint
    })

    if (!response.ok) {
      throw new ApiError(response.status, `HTTP error! status: ${response.status}`)
    }

    const data: ApiResponse = await response.json()

    if (data.code !== 200) {
      throw new ApiError(data.code, `API error! code: ${data.code}`)
    }

    return data
  } catch (error) {
    console.error('Error fetching appointment details:', error)
    throw error
  }
}

interface ScheduleAppointmentRequest {
  appointmentUUID: string
  scoutUUID?: string
  option: number
  scheduleStartTime: string // ISO string format
}

interface ScheduleAppointmentResponse {
  code: number
  data: {
    uuid: string
    buildingUUID: string
    scheduleStartTime: string | null
    createdAt: string
    updatedAt: string
    customerAccountUUID: string | null
    scoutUUID: string
    scoutName: string
    status: string
    sc2Url: string | null
    customerAddress: string
    customerName: string
    customerPhone: string
    customerEmail: string
    dataCollected: boolean
    residentialUnits: number
  }
}

export const scheduleAppointment = async (
  appointmentUuid: string,
  request: ScheduleAppointmentRequest
): Promise<ScheduleAppointmentResponse> => {
  try {
    const url = `${SCOUT_SERVICE_BASE_URL}/user/v1/public/onsite-appointment/scheduling/${appointmentUuid}`

    const response = await httpClient.fetch(url, {
      method: 'POST',
      body: JSON.stringify({
        appointmentUUID: request.appointmentUUID, // Note: Backend expects 3 U's
        scoutUUID: request.scoutUUID || null,
        option: request.option,
        scheduleStartTime: request.scheduleStartTime,
      }),
      skipAuth: true, // Public endpoint
    })

    if (!response.ok) {
      throw new ApiError(response.status, `HTTP error! status: ${response.status}`)
    }

    const data: ScheduleAppointmentResponse = await response.json()

    if (data.code !== 200) {
      throw new ApiError(data.code, `API error! code: ${data.code}`)
    }

    return data
  } catch (error) {
    console.error('Error scheduling appointment:', error)
    throw error
  }
}

interface TrackingResponse {
  code: number
  message?: string
}

export const trackAppointmentAccess = async (
  appointmentUuid: string,
  scoutUuid: string
): Promise<TrackingResponse> => {
  try {
    const url = `${SCOUT_SERVICE_BASE_URL}/user/v1/public/onsite-appointment/scheduling/track/${appointmentUuid}`

    const response = await httpClient.fetch(url, {
      method: 'POST',
      body: JSON.stringify({
        status: 'accessed',
        scoutUUID: scoutUuid,
      }),
      skipAuth: true, // Public endpoint
    })

    if (!response.ok) {
      throw new ApiError(response.status, `HTTP error! status: ${response.status}`)
    }

    const data: TrackingResponse = await response.json()

    return data
  } catch (error) {
    console.error('Error tracking appointment access:', error)
    // Don't throw error for tracking - we don't want to break the page load
    return {
      code: 500,
      message: 'Failed to track access',
    }
  }
}
